import { Link } from "react-router-dom";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { WHATSAPP_URL, WHATSAPP_NUMBER } from "../lib/site";
import { Logo } from "./Logo";

const PAGES = {
  terms: {
    title: "Terms of Service",
    updated: "Last updated: October 2026",
    sections: [
      {
        h: "About this program",
        p: "Digital Product Academy (DPA), founded by Anuj Tiwari, offers live online training programs, including the 2-Day AI Digital Product Business Bootcamp taught in Hinglish.",
      },
      {
        h: "Enrollment & payment",
        p: "Registration is completed through our external checkout partner (Superprofile). Your seat is confirmed once the payment is successful. Joining instructions and community details are shared with registered participants on WhatsApp and email.",
      },
      {
        h: "Personal access",
        p: "Joining links and community access are personal to the registered participant. Please do not share, resell or redistribute them.",
      },
      {
        h: "Educational purpose",
        p: "The bootcamp is an educational program. It teaches skills and processes, but does not guarantee any earnings or business outcomes. Individual results vary.",
      },
      {
        h: "Materials",
        p: "All session content and materials are the intellectual property of Digital Product Academy and are provided for your personal learning use only.",
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: October 2026",
    sections: [
      {
        h: "What we collect",
        p: "When you register, your contact and payment details are collected by our checkout partner (Superprofile) and its payment processors. We receive the details needed to deliver your bootcamp access, such as your name, email and phone number.",
      },
      {
        h: "How we use it",
        p: "We use your details to send joining instructions, bootcamp updates and support messages on WhatsApp and email. We do not sell your personal information to anyone.",
      },
      {
        h: "Third-party services",
        p: "Payments and messaging are handled by third-party platforms (Superprofile, its payment partners, WhatsApp and email providers). Their own privacy policies apply to their services.",
      },
      {
        h: "Your choices",
        p: `You can ask us to update or delete your contact details at any time by messaging us on WhatsApp at ${WHATSAPP_NUMBER}.`,
      },
    ],
  },
  refund: {
    title: "Refund Policy",
    updated: "Last updated: October 2026",
    sections: [
      {
        h: "Before you enroll",
        p: `If you have any questions about refunds, please message us on WhatsApp at ${WHATSAPP_NUMBER} before completing your registration and we will clarify the current policy for your batch.`,
      },
      {
        h: "Refund requests",
        p: "Refund requests are reviewed individually. Message us on WhatsApp with your registered name and payment details, and our team will respond and guide you through the process.",
      },
    ],
  },
  contact: {
    title: "Contact Us",
    updated: "",
    sections: [
      {
        h: "WhatsApp support",
        p: `The fastest way to reach us is WhatsApp at ${WHATSAPP_NUMBER}. Ask us anything about the bootcamp — schedule, content, access or payments.`,
      },
      {
        h: "About",
        p: "Digital Product Academy (DPA) is an online education brand founded by Anuj Tiwari, focused on practical digital product and AI training in Hinglish.",
      },
    ],
  },
};

export default function LegalPage({ type }) {
  const page = PAGES[type];
  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 border-b border-[#E9DFFC]/80 bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <Link to="/" aria-label="Back to home">
            <Logo />
          </Link>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E9DFFC] px-4 py-2 text-sm font-semibold text-[#171321] transition-colors hover:bg-[#F6F1FF]"
          >
            <ArrowLeft size={15} /> Back
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-16 sm:py-20" data-testid={`legal-${type}-page`}>
        <h1 className="text-3xl font-extrabold tracking-tight text-[#171321] sm:text-4xl">{page.title}</h1>
        {page.updated && <p className="mt-2 font-mono text-xs text-[#777181]">{page.updated}</p>}
        <div className="mt-10 space-y-8">
          {page.sections.map((s) => (
            <section key={s.h}>
              <h2 className="text-lg font-extrabold text-[#171321]">{s.h}</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-[#777181]">{s.p}</p>
            </section>
          ))}
        </div>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="legal-whatsapp-cta"
          className="mt-12 inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-[#9B5CF0] via-[#7C3AED] to-[#6D28D9] px-7 py-3.5 text-[15px] font-bold text-white shadow-[0_10px_25px_-5px_rgba(124,58,237,0.45)] transition-transform duration-200 hover:scale-[1.03]"
        >
          <MessageCircle size={17} /> Message us on WhatsApp
        </a>
      </main>
    </div>
  );
}
